/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 筛选操作 Hook
 */
import { useState, useCallback } from 'react'
import { FilterState } from '../types'

/**
 * 筛选操作 Hook
 * 管理各种筛选弹窗的显示状态
 */
export const useFilterActions = () => {
  const [showDatePickerPane, setShowDatePickerPane] = useState(false)
  const [showMemberPickerModal, setShowMemberPickerModal] = useState(false)
  const [showWatermarkModal, setShowWatermarkModal] = useState(false)
  const [showLocationPickerModal, setShowLocationPickerModal] = useState(false)
  const [showTagPickerModal, setShowTagPickerModal] = useState(false)

  // 切换筛选弹窗
  const toggleFilterModal = useCallback(
    (type: 'time' | 'member' | 'watermark' | 'location' | 'tag') => {
      switch (type) {
        case 'time':
          setShowDatePickerPane((prev) => !prev)
          setShowMemberPickerModal(false)
          setShowWatermarkModal(false)
          setShowLocationPickerModal(false)
          setShowTagPickerModal(false)
          break
        case 'member':
          setShowMemberPickerModal((prev) => !prev)
          setShowDatePickerPane(false)
          setShowWatermarkModal(false)
          setShowLocationPickerModal(false)
          setShowTagPickerModal(false)
          break
        case 'watermark':
          setShowWatermarkModal((prev) => !prev)
          setShowDatePickerPane(false)
          setShowMemberPickerModal(false)
          setShowLocationPickerModal(false)
          setShowTagPickerModal(false)
          break
        case 'location':
          setShowLocationPickerModal((prev) => !prev)
          setShowDatePickerPane(false)
          setShowMemberPickerModal(false)
          setShowWatermarkModal(false)
          setShowTagPickerModal(false)
          break
        case 'tag':
          setShowTagPickerModal((prev) => !prev)
          setShowDatePickerPane(false)
          setShowMemberPickerModal(false)
          setShowWatermarkModal(false)
          setShowLocationPickerModal(false)
          break
      }
    },
    []
  )

  // 关闭所有弹窗
  const closeAllModals = useCallback(() => {
    setShowDatePickerPane(false)
    setShowMemberPickerModal(false)
    setShowWatermarkModal(false)
    setShowLocationPickerModal(false)
    setShowTagPickerModal(false)
  }, [])

  // 处理日期变化
  const handleDatesChange = useCallback(
    (
      startDate: any,
      endDate: any,
      onUpdate: (updates: Partial<FilterState>) => void
    ) => {
      setShowDatePickerPane(false)
      onUpdate({
        times: startDate.isAfter(endDate)
          ? [endDate, startDate]
          : [startDate, endDate]
      })
    },
    []
  )

  // 处理成员变化
  const handleMemberChange = useCallback(
    (
      params: {
        selectedDepartmentUsers: any[]
        selectedDepartments: any[]
      },
      onUpdate: (updates: Partial<FilterState>) => void
    ) => {
      setShowMemberPickerModal(false)
      onUpdate({
        departmentUsers: params.selectedDepartmentUsers,
        departments: params.selectedDepartments
      })
    },
    []
  )

  // 处理水印变化
  const handleWatermarkChange = useCallback(
    (
      checkedList: string[],
      onUpdate: (updates: Partial<FilterState>) => void
    ) => {
      setShowWatermarkModal(false)
      onUpdate({ watermarks: checkedList })
    },
    []
  )

  // 处理地点变化
  const handleLocationChange = useCallback(
    (
      location: { name: string; detail: string; type: number },
      onUpdate: (updates: Partial<FilterState>) => void
    ) => {
      setShowLocationPickerModal(false)
      onUpdate({ locations: [location] })
    },
    []
  )

  // 处理标签变化
  const handleTagChange = useCallback(
    (
      labelIDs: string[],
      labelMatchAll: boolean,
      onUpdate: (updates: Partial<FilterState>) => void
    ) => {
      setShowTagPickerModal(false)
      onUpdate({ labelIDs, labelMatchAll })
    },
    []
  )

  return {
    showDatePickerPane,
    showMemberPickerModal,
    showWatermarkModal,
    showLocationPickerModal,
    showTagPickerModal,
    toggleFilterModal,
    closeAllModals,
    handleDatesChange,
    handleMemberChange,
    handleWatermarkChange,
    handleLocationChange,
    handleTagChange
  }
}
